import { axiosInstance } from '@/rest'

/**
 * Creates an API interface for interacting with the search engine.
 * @returns {Object} The API object with methods `searchV2`, `listProfilesV3`, `clear`, and `load`.
 */
const createSearchEngineApi = () => {
  return {
    /**
     * Fetch user accounts based on the search query using v2 search API.
     * @param {Object} query - The search query object.
     * @param {string} query.q - The text to search for.
     * @param {boolean} [query.include_closed] - Whether to include closed accounts in the results.
     * @returns {Promise<{
     *           user_profile_id: string,
     *           customer_type: string,
     *           account_type: string,
     *           account_number: string,
     *           email: string,
     *           given_name: string,
     *           family_name: string,
     *           phone_number: string,
     *           date_of_birth: string,
     *           account_status: string,
     *           owner: string,
     *           score: number,
     *  }[]>} List of user profiles.
     */
    searchV2: (query) =>
      axiosInstance.get('v2/search/search', {
        params: { q: query.q, include_closed: query.include_closed },
      }),
    /**
     * Fetch every user profile, unfiltered, for client-side searching.
     * One entry per holder: a joint account has one for the primary owner
     * and one per co-applicant with a login, so `(user_profile_id, owner)`
     * is the unique key.
     * @param {Object} [query]
     * @param {boolean} [query.include_closed] - Include accounts closed by the firm.
     * @returns {Promise<{
     *           user_profile_id: string,
     *           owner: string,
     *           customer_type: string,
     *           account_type: string,
     *           account_number: string,
     *           account_number_last4: string,
     *           account_status: string,
     *           email: string,
     *           given_name: string,
     *           family_name: string,
     *           phone_number: string,
     *           phone_numbers_national: string[],
     *           ssn_last4: string,
     *           date_of_birth: string,
     *  }[]>} Every profile entry, ordered by family name then given name.
     */
    listProfilesV3: (query = {}) =>
      axiosInstance.get('v3/search/profiles', {
        params: { include_closed: query.include_closed },
      }),
    /**
     * Clear the search engine data.
     * @returns {Promise} A promise that resolves once the data has been cleared.
     */
    clear: () => axiosInstance.delete('engine/clear'),

    /**
     * Load data into the search engine.
     * @returns {Promise} A promise that resolves once the data has been loaded.
     */
    load: () => axiosInstance.post('engine/load'),
  }
}

const searchEngineApi = createSearchEngineApi()

export { searchEngineApi }
